import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { of, Observable } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ErrorHandlingService } from '../../services/error-handling/error-handling.service';
import { SpinnerService } from '../../services/spinner/spinner.service';
import { HttpHeaderConstants } from '../../services/http-header.constant';
import { DatacloudPicklist } from '../../models/appcloud/datacloud-picklist.model';

@Injectable({
    providedIn: 'root',
})
export class DatacloudPicklistService {
    readonly #errorHandlingService = inject(ErrorHandlingService);
    readonly #http = inject(HttpClient);
    readonly #spinnerService = inject(SpinnerService);

    private DATACLOUD_PICKLIST_URL: string = environment.publicApiHost + '/appcloud/datacloud-picklists';

    getById(id: number, additionalHeaders: any = {}): Observable<DatacloudPicklist> {
        this.#spinnerService.addProcess('DatacloudPicklistService.getById.' + id, 'Loading your picklist...');
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };

        return this.#http.get<DatacloudPicklist>(`${this.DATACLOUD_PICKLIST_URL}/${id}`, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while retrieving picklist data from Relationship One. Please try again later.'
                );
                return of<DatacloudPicklist>();
            }),
            finalize(() => this.#spinnerService.completeProcess('DatacloudPicklistService.getById.' + id))
        );
    }

    findAll(eloquainstallId: number, additionalHeaders: any = {}): Observable<DatacloudPicklist[]> {
        this.#spinnerService.addProcess(
            'DatacloudPicklistService.findAll.' + eloquainstallId,
            'Loading your picklists...'
        );
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
            params: new HttpParams().set('eloquainstallId', eloquainstallId.toString()),
        };

        return this.#http.get<DatacloudPicklist[]>(this.DATACLOUD_PICKLIST_URL, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while retrieving picklist data from Relationship One. Please try again later.'
                );
                return of<DatacloudPicklist[]>();
            }),
            finalize(() => this.#spinnerService.completeProcess('DatacloudPicklistService.findAll.' + eloquainstallId))
        );
    }

    create(picklist: DatacloudPicklist, additionalHeaders: any = {}): Observable<DatacloudPicklist> {
        this.#spinnerService.addProcess('DatacloudPicklistService.create', 'Creating your datacloud picklist...');

        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };

        return this.#http.post<DatacloudPicklist>(this.DATACLOUD_PICKLIST_URL, picklist, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while saving your picklist. Please try again later.'
                );
                return of<DatacloudPicklist>();
            }),
            finalize(() => {
                this.#spinnerService.completeProcess('DatacloudPicklistService.create');
            })
        );
    }

    update(id: number, picklist: DatacloudPicklist, additionalHeaders: any = {}) {
        this.#spinnerService.addProcess('DatacloudPicklistService.update.' + id, 'Updating your picklist...');
        const httpOptions = {
            headers: new HttpHeaders({
                ...HttpHeaderConstants.mergePatch,
                ...additionalHeaders,
            }),
        };

        return this.#http.patch<DatacloudPicklist>(this.DATACLOUD_PICKLIST_URL + '/' + id, picklist, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while updating your picklist. Please try again later.'
                );
                return of<DatacloudPicklist>();
            }),
            finalize(() => this.#spinnerService.completeProcess('DatacloudPicklistService.update.' + id))
        );
    }
}
